import { shouldRetrySilentRenew } from "./shouldRetrySilentRenew";

/**
 * Base backoff between silent-renew retries (exponential doubling: 400ms,
 * 800ms, 1.6s, 3.2s). The total (~6s for the default 4 retries) stays well
 * inside the 60s pre-expiry renewal window so retries never outlive the
 * access token.
 */
export const SILENT_RENEW_RETRY_BASE_MS = 400;

const realSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Run a silent token renewal, retrying TRANSIENT failures (network blips,
 * Keycloak `temporarily_unavailable`/`server_error`) with short exponential
 * backoff before letting it reject. A genuine session-end (invalid_grant /
 * "Token is not active"), a timeout (oidc-client-ts self-heals those itself),
 * or any other non-transient shape is NOT retried — the ORIGINAL error is
 * rethrown unchanged so downstream handling (silentRenewError / the redirect
 * path, and oidc's own `instanceof ErrorTimeout` self-heal) behaves exactly as
 * before. Addresses CUSTOMER-UI-56: a single transient blip used to bounce an
 * active user to login (and onto a stale Exchange/Pay quote).
 *
 * `sleep` is injectable solely so the backoff is unit-testable without real
 * timers; production callers use the default.
 */
export async function renewWithRetry<T>(
  run: () => Promise<T>,
  sleep: (ms: number) => Promise<void> = realSleep
): Promise<T> {
  let attempt = 0;
  for (;;) {
    try {
      return await run();
    } catch (error) {
      attempt += 1;
      if (!shouldRetrySilentRenew(error, attempt)) throw error;
      await sleep(SILENT_RENEW_RETRY_BASE_MS * 2 ** (attempt - 1));
    }
  }
}
